import React, { useState, useEffect, useMemo } from 'react';
import { db, auth, handleFirestoreError, OperationType, isQuotaError } from '../firebase';
import { collection, query, where, getDocs, doc, setDoc, serverTimestamp, onSnapshot, orderBy } from 'firebase/firestore';
import { getHolidays, getHolidayForDate } from '../services/holidayService';
import { syncRegularToHoliday } from '../services/attendanceSyncService';
import { updateStudentStat, refreshDailyStats, clearDailyStatsCache } from '../services/statsService';
import { getCachedStudents, getCachedClasses, getCachedQuery, clearDataCache } from '../services/dataCacheService';
import { useAcademicYear } from '../context/AcademicYearContext';
import { isSameClass } from '../services/academicYearService';
import { Student, SchoolClass } from '../types';
import { Check, Save, Loader2, AlertCircle, UserCheck, UserX, ChevronRight, ChevronLeft, Calendar as CalendarIcon, RefreshCw, Users, Bus, Truck, School } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

type AttendanceRow = Student & {
  present: boolean;
  notes: string;
};

interface AttendanceProps {
  userRole?: string | null;
  assignedClassId?: string | null;
  initialClassId?: string | null;
}

const Attendance: React.FC<AttendanceProps> = ({ userRole, assignedClassId, initialClassId }) => {
  const { selectedYear, currentYearObj } = useAcademicYear();
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [classesLoading, setClassesLoading] = useState(true);
  const [selectedClassId, setSelectedClassId] = useState(initialClassId || 'all');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [students, setStudents] = useState<AttendanceRow[]>([]);
  const [attendanceRecords, setAttendanceRecords] = useState<Record<string, any>>({});
  const [localOverrides, setLocalOverrides] = useState<Record<string, { present: boolean; notes: string }>>({});
  const [savingStudentIds, setSavingStudentIds] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkReason, setBulkReason] = useState('חג');
  const [customReason, setCustomReason] = useState('');
  const [isWeekend, setIsWeekend] = useState(false);
  const [holidays, setHolidays] = useState<any[]>([]);
  const [attendanceFilter, setAttendanceFilter] = useState<'all' | 'present' | 'absent'>('all');

  // Transport and grouping state
  const [viewGrouping, setViewGrouping] = useState<'class' | 'transport'>('class');
  const [selectedTransportFilter, setSelectedTransportFilter] = useState<string>('all');
  const [transportLines, setTransportLines] = useState<string[]>([]);

  useEffect(() => {
    getHolidays().then(setHolidays);
  }, []);

  const selectedClass = useMemo(
    () => selectedClassId === 'all' ? { id: 'all', name: 'כל התלמידים' } : classes.find((c) => c.id === selectedClassId),
    [classes, selectedClassId]
  );

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    if (initialClassId) {
      setSelectedClassId(initialClassId);
    }
  }, [initialClassId]);

  useEffect(() => {
    if (userRole === 'teacher' && assignedClassId) {
      setSelectedClassId(assignedClassId);
    }
  }, [userRole, assignedClassId]);

  useEffect(() => {
    if (selectedClassId && selectedDate) {
      fetchStudents(selectedClassId);
    }
  }, [selectedClassId, selectedYear]);

  useEffect(() => {
    if (selectedDate) {
      const date = new Date(selectedDate);
      const day = date.getDay();
      setIsWeekend(day === 5 || day === 6);

      setLocalOverrides({}); // Clear overrides when date changes
      
      // OPTIMIZATION: Scope attendance query to selected class if not 'all' to reduce reads
      let q = query(
        collection(db, 'studentAttendance'),
        where('date', '==', selectedDate)
      );
      
      if (selectedClassId !== 'all') {
        q = query(q, where('classId', '==', selectedClassId));
      }

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const records: Record<string, any> = {};
        snapshot.forEach(docSnap => {
          const data = docSnap.data();
          if (data.studentId) {
            records[data.studentId] = data;
          }
        });
        setAttendanceRecords(records);
      }, (error) => {
        if (isQuotaError(error)) {
          setMessage({ text: 'מכסת השימוש בבסיס הנתונים הסתיימה להיום. הנתונים עשויים להיות לא מעודכנים.', type: 'error' });
          unsubscribe();
        }
        console.error("Error listening to attendance:", error);
      });

      return () => unsubscribe();
    }
  }, [selectedDate, selectedClassId]);

  const fetchClasses = async (forceRefresh = false) => {
    setClassesLoading(true);
    try {
      const classList = await getCachedClasses(forceRefresh);
      const sortedClasses = classList.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'he', { numeric: true }));
      setClasses(sortedClasses);
      
      if (userRole !== 'teacher' && !selectedClassId && !initialClassId) {
        setSelectedClassId('all');
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, 'classes');
      console.error("Error fetching classes:", error);
      setMessage({ text: 'שגיאה בטעינת הכיתות', type: 'error' });
    } finally {
      setClassesLoading(false);
    }
  };

  const fetchStudents = async (classId: string, forceRefresh = false) => {
    setLoading(true);
    try {
      const allStudents = await getCachedStudents(forceRefresh, selectedYear);
      
      // Filter only active students for the selected academic year
      const activeStudents = allStudents.filter(s => s.active !== false);

      // Collect all unique transport lines from active students
      const linesFromStudents = Array.from(new Set(
        activeStudents.map(s => s.transportName?.trim()).filter(Boolean) as string[]
      ));

      try {
        const tSnap = await getCachedQuery('transportLines_list', query(collection(db, 'transportLines'), orderBy('name')));
        const dbLines = tSnap.map((d: any) => d.name?.trim()).filter(Boolean);
        const combined = Array.from(new Set([...dbLines, ...linesFromStudents])).sort((a, b) => a.localeCompare(b, 'he'));
        setTransportLines(combined);
      } catch {
        setTransportLines(linesFromStudents.sort((a, b) => a.localeCompare(b, 'he')));
      }

      const studentList: AttendanceRow[] = activeStudents
        .filter(s => {
          if (classId === 'all') return true;
          if (s.classId === classId || s.className === classId) return true;
          const targetClass = classes.find(c => c.id === classId || c.name === classId);
          return isSameClass(
            { id: s.classId, name: s.className },
            { id: targetClass?.id || classId, name: targetClass?.name || classId }
          );
        })
        .map(s => ({
          id: s.id,
          fullName: s.fullName,
          nationalId: s.nationalId,
          classId: s.classId,
          className: s.className,
          transportName: s.transportName || '',
          escortName: s.escortName || '',
          active: s.active,
          present: true,
          notes: '',
        })).sort((a, b) => {
          const classCompare = (a.className || '').localeCompare(b.className || '', 'he', { numeric: true });
          if (classCompare !== 0) return classCompare;
          return (a.fullName || '').localeCompare(b.fullName || '', 'he');
        });

      setStudents(studentList);
      setLastUpdated(new Date());
    } catch (error) {
      if (isQuotaError(error)) {
        setMessage({ text: 'מכסת השימוש בבסיס הנתונים הסתיימה להיום.', type: 'error' });
      } else {
        handleFirestoreError(error, OperationType.LIST, 'students');
      }
      console.error("Error fetching students:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchClasses(true);
    fetchStudents(selectedClassId, true);
  };

  const studentsWithAttendance = useMemo(() => {
    const holiday = getHolidayForDate(selectedDate, holidays);
    return students.map(student => {
      const record = attendanceRecords[student.id];
      const override = localOverrides[student.id];
      
      const defaultPresent = holiday ? false : true;
      const defaultNotes = holiday ? `חופשה: ${holiday.name}` : '';

      return {
        ...student,
        present: override ? override.present : (record ? record.present : defaultPresent),
        notes: override ? override.notes : (record ? record.notes : (record ? '' : defaultNotes))
      };
    });
  }, [students, attendanceRecords, localOverrides, selectedDate, holidays]);

  const filteredStudents = useMemo(() => {
    let list = studentsWithAttendance;

    // Filter by transport if selected
    if (selectedTransportFilter !== 'all') {
      if (selectedTransportFilter === '__NONE__') {
        list = list.filter(s => !s.transportName);
      } else {
        list = list.filter(s => s.transportName === selectedTransportFilter);
      }
    }

    // Filter by attendance status
    if (attendanceFilter === 'present') {
      list = list.filter(s => s.present);
    } else if (attendanceFilter === 'absent') {
      list = list.filter(s => !s.present);
    }

    // Sort based on current view grouping
    return [...list].sort((a, b) => {
      if (viewGrouping === 'transport') {
        const transA = a.transportName || 'ללא הסעה';
        const transB = b.transportName || 'ללא הסעה';
        const transComp = transA.localeCompare(transB, 'he');
        if (transComp !== 0) return transComp;
        const classComp = (a.className || '').localeCompare(b.className || '', 'he', { numeric: true });
        if (classComp !== 0) return classComp;
        return (a.fullName || '').localeCompare(b.fullName || '', 'he');
      } else {
        const classComp = (a.className || '').localeCompare(b.className || '', 'he', { numeric: true });
        if (classComp !== 0) return classComp;
        return (a.fullName || '').localeCompare(b.fullName || '', 'he');
      }
    });
  }, [studentsWithAttendance, attendanceFilter, selectedTransportFilter, viewGrouping]);

  const toggleAttendance = async (id: string) => {
    const student = studentsWithAttendance.find(s => s.id === id);
    if (!student) return;

    const newPresent = !student.present;
    const currentNotes = localOverrides[id]?.notes ?? student.notes;

    // 1. Optimistic UI update immediately
    setLocalOverrides(prev => ({
      ...prev,
      [id]: {
        present: newPresent,
        notes: currentNotes
      }
    }));

    // 2. Set per-student saving indicator
    setSavingStudentIds(prev => ({ ...prev, [id]: true }));
    
    // 3. Background save without disrupting UI or scrolling
    try {
      await saveIndividualAttendance(id, newPresent, currentNotes);
    } catch (err) {
      console.error('Error auto-saving attendance toggle:', err);
    } finally {
      setSavingStudentIds(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const saveIndividualAttendance = async (studentId: string, present: boolean, notes: string) => {
    const student = students.find(s => s.id === studentId);
    if (!student) return;

    try {
      const date = selectedDate;
      const updatedBy = auth.currentUser?.email || "unknown";
      const attendanceId = `${date}_${student.classId}_${student.id}`;
      
      const prevRecord = attendanceRecords[studentId];
      const wasPresent = prevRecord?.present;

      await setDoc(doc(db, 'studentAttendance', attendanceId), {
        date,
        classId: student.classId,
        className: student.className,
        studentId: student.id,
        studentName: student.fullName,
        transportName: student.transportName || '',
        escortName: student.escortName || '',
        present,
        notes,
        updatedBy,
        updatedAt: new Date().toISOString(),
        timestamp: serverTimestamp()
      });

      await updateStudentStat(date, student.classId, wasPresent, present);
      clearDailyStatsCache(selectedYear);

      const holidays = await getHolidays();
      const holiday = getHolidayForDate(date, holidays);
      if (holiday) {
        await syncRegularToHoliday(student.id, 'student', date, present ? 'present' : 'absent', holiday.id);
      }
    } catch (error) {
      console.error("Error auto-saving attendance:", error);
    }
  };

  const autoSaveTimerRef = React.useRef<Record<string, NodeJS.Timeout>>({});

  const updateNotes = (id: string, notes: string) => {
    const student = studentsWithAttendance.find(s => s.id === id);
    if (!student) return;

    // Immediate local state update
    setLocalOverrides(prev => ({
      ...prev,
      [id]: {
        present: prev[id]?.present ?? student.present,
        notes: notes
      }
    }));
    
    if (autoSaveTimerRef.current[id]) {
      clearTimeout(autoSaveTimerRef.current[id]);
    }

    autoSaveTimerRef.current[id] = setTimeout(async () => {
      setSavingStudentIds(prev => ({ ...prev, [id]: true }));
      try {
        await saveIndividualAttendance(id, localOverrides[id]?.present ?? student.present, notes);
      } catch (err) {
        console.error('Error saving note:', err);
      } finally {
        setSavingStudentIds(prev => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
      }
    }, 700);
  };

  const handleSave = async () => {
    if (!selectedClass || studentsWithAttendance.length === 0) return;
    
    const scrollPos = window.scrollY;
    setSaving(true);
    setMessage({ text: '', type: '' });
    
    try {
      const date = selectedDate;
      const updatedBy = auth.currentUser?.email || "unknown";
      const { writeBatch } = await import('firebase/firestore');
      const batch = writeBatch(db);

      // Save all students in the class so every student has an explicit attendance record
      const studentsToSave = studentsWithAttendance;

      studentsToSave.forEach(student => {
        const attendanceId = `${date}_${student.classId}_${student.id}`;
        const docRef = doc(db, 'studentAttendance', attendanceId);
        batch.set(docRef, {
          date,
          classId: student.classId,
          className: student.className,
          studentId: student.id,
          studentName: student.fullName,
          transportName: student.transportName || '',
          escortName: student.escortName || '',
          present: student.present,
          notes: student.notes,
          updatedBy,
          updatedAt: new Date().toISOString(),
          timestamp: serverTimestamp()
        });
      });

      const holidays = await getHolidays();
      const holiday = getHolidayForDate(date, holidays);
      
      if (holiday) {
        studentsToSave.forEach(student => {
          const syncDocId = `${holiday.id}_${date}_${student.id}`;
          const syncDocRef = doc(db, 'holidayAttendance', syncDocId);
          batch.set(syncDocRef, {
            holidayId: holiday.id,
            date,
            targetId: student.id,
            targetType: 'student',
            status: student.present ? 'present' : 'absent',
            lastUpdated: serverTimestamp()
          }, { merge: true });
        });
      }

      await batch.commit();
      clearDataCache();
      setLocalOverrides({});
      
      await refreshDailyStats(date, selectedYear, true);
      
      setMessage({ text: `הנוכחות נשמרה בהצלחה (${studentsToSave.length} תלמידים)!`, type: 'success' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } catch (error) {
      if (isQuotaError(error)) {
        setMessage({ text: 'מכסת הכתיבה בבסיס הנתונים הסתיימה להיום.', type: 'error' });
      } else {
        handleFirestoreError(error, OperationType.WRITE, 'studentAttendance');
        setMessage({ text: 'שגיאה בשמירת הנתונים', type: 'error' });
      }
      console.error("Error saving attendance:", error);
    } finally {
      setSaving(false);
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollPos, behavior: 'instant' });
      });
    }
  };

  const applyBulkAbsence = () => {
    const finalReason = bulkReason === 'אחר' ? customReason : bulkReason;
    if (bulkReason === 'אחר' && !customReason) return;

    const overrides: Record<string, { present: boolean; notes: string }> = {};
    students.forEach(s => {
      overrides[s.id] = {
        present: false,
        notes: finalReason
      };
    });
    
    setLocalOverrides(prev => ({ ...prev, ...overrides }));
    setShowBulkModal(false);
    setCustomReason('');
    setMessage({ text: `סומנה היעדרות גורפת: ${finalReason}`, type: 'success' });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  const markAllPresent = () => {
    const overrides: Record<string, { present: boolean; notes: string }> = {};
    students.forEach(s => {
      overrides[s.id] = {
        present: true,
        notes: ''
      };
    });
    
    setLocalOverrides(prev => ({ ...prev, ...overrides }));
    setMessage({ text: 'כל התלמידים סומנו כנוכחים', type: 'success' });
    setTimeout(() => setMessage({ text: '', type: '' }), 3000);
  };

  const changeDate = (days: number) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + days);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl">
              <UserCheck size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">נוכחות תלמידים</h2>
              <div className="flex items-center gap-2">
                <p className="text-sm text-slate-500">בחר כיתה וסמן נוכחות יומית</p>
                <span className="text-[11px] font-bold text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-md">
                  {currentYearObj.name}
                </span>
                {lastUpdated && (
                  <span className="text-[10px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
                    עודכן לאחרונה: {lastUpdated.toLocaleTimeString('he-IL')}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="inline-flex items-center gap-2 bg-white text-slate-600 border border-slate-200 px-4 py-2 rounded-xl font-bold hover:bg-slate-50 transition-all shadow-sm disabled:opacity-50"
              title="רענן נתונים מהשרת"
            >
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
              <span>רענן</span>
            </button>
            
            {/* Date Navigation */}
          <div className="flex items-center bg-slate-50 rounded-2xl border border-slate-200 p-1 shadow-sm">
            <button
              onClick={() => changeDate(1)}
              className="p-2 hover:bg-white hover:shadow-sm rounded-xl text-slate-600 transition-all"
              title="יום הבא"
            >
              <ChevronRight size={20} />
            </button>
            
            <div className="relative flex items-center px-4 border-x border-slate-200">
              <CalendarIcon size={16} className="text-slate-400 ml-2" />
              <div className="flex flex-col items-start min-w-[100px]">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="bg-transparent border-none text-sm font-bold text-slate-700 focus:ring-0 p-0 cursor-pointer h-5"
                />
                <span className="text-[10px] text-indigo-500 font-bold leading-none">
                  {new Date(selectedDate).toLocaleDateString('he-IL', { weekday: 'long' })}
                </span>
              </div>
            </div>

            <button
              onClick={() => changeDate(-1)}
              className="p-2 hover:bg-white hover:shadow-sm rounded-xl text-slate-600 transition-all"
              title="יום קודם"
            >
              <ChevronLeft size={20} />
            </button>
          </div>
        </div>
      </div>

      {isWeekend && (
          <div className="bg-amber-50 border border-amber-200 p-4 rounded-2xl flex items-center gap-3 text-amber-800 animate-in fade-in slide-in-from-top-2">
            <AlertCircle size={24} className="text-amber-500 shrink-0" />
            <div>
              <h4 className="font-bold text-sm">שים לב: התאריך הנבחר הוא יום {new Date(selectedDate).getDay() === 5 ? 'שישי' : 'שבת'}</h4>
              <p className="text-xs opacity-90">בית הספר אינו עובד בימי שישי ושבת. מומלץ לוודא את התאריך לפני הדיווח.</p>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-4">
          {/* Top row: View mode toggle (Class vs Transport) & Transport / Class quick filter */}
          <div className="flex flex-wrap items-center justify-between gap-4 pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-2xl">
              <button
                type="button"
                onClick={() => setViewGrouping('class')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                  viewGrouping === 'class'
                    ? 'bg-white text-indigo-600 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <School size={16} />
                <span>חלוקה לפי כיתות</span>
              </button>
              <button
                type="button"
                onClick={() => setViewGrouping('transport')}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${
                  viewGrouping === 'transport'
                    ? 'bg-white text-amber-600 shadow-sm'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                <Bus size={16} />
                <span>חלוקה לפי מסיעים</span>
              </button>
            </div>

            {/* Transport Filter Dropdown */}
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-500 flex items-center gap-1">
                <Bus size={14} className="text-amber-500" />
                סינון לפי מסיע:
              </span>
              <select
                value={selectedTransportFilter}
                onChange={(e) => setSelectedTransportFilter(e.target.value)}
                className="bg-slate-50 border border-slate-200 text-xs font-bold rounded-xl px-3 py-2 outline-none text-slate-700 hover:border-amber-300 focus:ring-2 focus:ring-amber-200"
              >
                <option value="all">כל המסיעים (ללא סינון)</option>
                <option value="__NONE__">תלמידים ללא הסעה</option>
                {transportLines.map(line => (
                  <option key={line} value={line}>{line}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap gap-2">
              {userRole !== 'teacher' && (
                <button
                  onClick={() => setSelectedClassId('all')}
                  className={`px-5 py-2.5 rounded-2xl transition-all font-medium text-sm ${
                    selectedClassId === 'all' 
                      ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100' 
                      : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-100'
                  }`}
                >
                  כל התלמידים
                </button>
              )}
              {classesLoading ? (
                <div className="text-slate-400 text-sm italic animate-pulse">טוען כיתות...</div>
              ) : classes.length === 0 ? (
                <div className="text-slate-400 text-sm italic">לא נמצאו כיתות</div>
              ) : (
                classes
                  .filter(cls => {
                    if (userRole === 'admin' || userRole === 'manager') return true;
                    return cls.id === assignedClassId;
                  })
                  .map(cls => (
                    <button
                      key={cls.id}
                      onClick={() => setSelectedClassId(cls.id)}
                      className={`px-5 py-2.5 rounded-2xl transition-all font-medium text-sm ${
                        selectedClassId === cls.id 
                          ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100' 
                          : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-100'
                      }`}
                    >
                      כיתה {cls.name}
                    </button>
                  ))
              )}
            </div>

            {studentsWithAttendance.length > 0 && (
              <div className="flex items-center gap-2 p-1 bg-slate-50 rounded-2xl border border-slate-100">
                <button
                  onClick={() => setAttendanceFilter('all')}
                  className={`px-4 py-1.5 rounded-xl text-xs font-bold transition-all ${
                    attendanceFilter === 'all' 
                      ? 'bg-white text-indigo-600 shadow-sm' 
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  הכל ({studentsWithAttendance.length})
                </button>
                <button
                  onClick={() => setAttendanceFilter('present')}
                  className={`px-4 py-1.5 rounded-xl text-xs font-bold transition-all ${
                    attendanceFilter === 'present' 
                      ? 'bg-emerald-500 text-white shadow-sm' 
                      : 'text-slate-500 hover:text-emerald-600'
                  }`}
                >
                  נוכחים ({studentsWithAttendance.filter(s => s.present).length})
                </button>
                <button
                  onClick={() => setAttendanceFilter('absent')}
                  className={`px-4 py-1.5 rounded-xl text-xs font-bold transition-all ${
                    attendanceFilter === 'absent' 
                      ? 'bg-rose-500 text-white shadow-sm' 
                      : 'text-slate-500 hover:text-rose-600'
                  }`}
                >
                  נעדרים ({studentsWithAttendance.filter(s => !s.present).length})
                </button>
              </div>
            )}
          </div>

          {studentsWithAttendance.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t border-slate-50">
                <div className="flex items-center gap-4">
                  <div className="flex gap-2">
                    <button
                      onClick={markAllPresent}
                      className="flex items-center gap-2 bg-emerald-50 text-emerald-700 border border-emerald-100 px-4 py-2 rounded-xl text-sm font-bold hover:bg-emerald-100 transition-all"
                    >
                      <Check size={18} />
                      סמן כולם כנוכח
                    </button>
                    <button
                      onClick={() => setShowBulkModal(true)}
                      className="flex items-center gap-2 bg-amber-50 text-amber-700 border border-amber-100 px-4 py-2 rounded-xl text-sm font-bold hover:bg-amber-100 transition-all"
                    >
                      <AlertCircle size={18} />
                      דיווח חריג
                    </button>
                    {Object.keys(localOverrides).length > 0 && (
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-xl text-sm font-bold hover:bg-indigo-700 shadow-md shadow-indigo-100 transition-all disabled:opacity-50"
                      >
                        {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                        <span>שמור שינויים ({Object.keys(localOverrides).length})</span>
                      </button>
                    )}
                  </div>
                {autoSaving && (
                  <div className="flex items-center gap-2 text-indigo-600 text-xs font-medium animate-pulse">
                    <Loader2 size={14} className="animate-spin" />
                    שומר אוטומטית...
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedClassId && (
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden"
        >
          {loading ? (
            <div className="p-16 flex flex-col items-center justify-center gap-4">
              <Loader2 className="animate-spin text-indigo-600" size={40} />
              <p className="text-slate-500 font-medium">טוען רשימת תלמידים...</p>
            </div>
          ) : students.length === 0 ? (
            <div className="p-16 text-center">
              <div className="inline-flex p-4 bg-slate-50 text-slate-400 rounded-full mb-4">
                <Users size={32} />
              </div>
              <p className="text-slate-500 font-medium">אין תלמידים פעילים רשומים בכיתה זו (או בסינון הנבחר)</p>
              <button 
                onClick={() => fetchStudents(selectedClassId, true)}
                className="mt-4 text-indigo-600 text-sm font-bold flex items-center gap-1 mx-auto hover:underline"
              >
                <RefreshCw size={14} />
                נסה לטעון מחדש
              </button>
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className="p-16 text-center">
              <div className="inline-flex p-4 bg-slate-50 text-slate-400 rounded-full mb-4">
                <AlertCircle size={32} />
              </div>
              <p className="text-slate-500 font-medium">
                {attendanceFilter === 'present' ? 'אין תלמידים נוכחים' : 'אין תלמידים נעדרים'}
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-right border-collapse">
                  <thead>
                    <tr className="bg-slate-50/50 border-b border-slate-100">
                      <th className="px-6 py-3 font-bold text-slate-700 text-sm">סטטוס ושם התלמיד</th>
                      <th className="px-6 py-3 font-bold text-slate-700 text-sm">כיתה</th>
                      <th className="px-6 py-3 font-bold text-slate-700 text-sm">מסיע / קו ומלווה</th>
                      <th className="px-6 py-3 font-bold text-slate-700 text-sm">תעודת זהות</th>
                      <th className="px-6 py-3 font-bold text-slate-700 text-sm">הערות</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {filteredStudents.map((student, index) => {
                      const showHeader = index === 0 || (
                        viewGrouping === 'transport'
                          ? (student.transportName || 'ללא הסעה') !== (filteredStudents[index - 1].transportName || 'ללא הסעה')
                          : (selectedClassId === 'all' && student.className !== filteredStudents[index - 1].className)
                      );
                      const isSaving = savingStudentIds[student.id];

                      return (
                        <React.Fragment key={student.id}>
                          {showHeader && (
                            <tr className="bg-slate-100/80 border-y border-slate-200">
                              <td colSpan={5} className="px-6 py-2.5 font-black text-slate-700 text-xs tracking-wider">
                                {viewGrouping === 'transport' ? (
                                  <div className="flex items-center gap-2 text-amber-800">
                                    <Bus size={15} />
                                    <span>מסיע / קו: {student.transportName || 'ללא שיוך להסעה'}</span>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2 text-indigo-800">
                                    <School size={15} />
                                    <span>כיתה {student.className}</span>
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                          <tr className="hover:bg-slate-50/30 transition-colors group">
                            <td className="px-6 py-2">
                              <div className="flex items-center gap-3">
                                <button
                                  onClick={() => toggleAttendance(student.id)}
                                  className={`flex items-center gap-2 px-3 py-1.5 rounded-xl transition-all border-2 shrink-0 ${
                                    student.present
                                      ? 'bg-emerald-50 border-emerald-100 text-emerald-700'
                                      : 'bg-red-50 border-red-100 text-red-700'
                                  }`}
                                >
                                  {student.present ? (
                                    <>
                                      <UserCheck size={14} />
                                      <span className="text-[11px] font-bold">נוכח</span>
                                    </>
                                  ) : (
                                    <>
                                      <UserX size={14} />
                                      <span className="text-[11px] font-bold">נעדר</span>
                                    </>
                                  )}
                                </button>
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-slate-900">{student.fullName}</span>
                                  {isSaving && (
                                    <Loader2 size={13} className="animate-spin text-indigo-500 shrink-0" title="שומר..." />
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-2">
                              <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-bold">
                                {student.className}
                              </span>
                            </td>
                            <td className="px-6 py-2">
                              {student.transportName ? (
                                <div className="flex flex-col gap-0.5">
                                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-md w-fit">
                                    <Bus size={12} className="text-amber-600" />
                                    <span>{student.transportName}</span>
                                  </span>
                                  {student.escortName && (
                                    <span className="text-[10px] text-slate-500 pr-1">
                                      מלווה: {student.escortName}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-[11px] text-slate-400">ללא הסעה</span>
                              )}
                            </td>
                            <td className="px-6 py-2 text-slate-500 font-mono text-sm">{student.nationalId}</td>
                            <td className="px-6 py-2">
                              <div className="relative flex items-center">
                                <input
                                  type="text"
                                  value={student.notes}
                                  onChange={(e) => updateNotes(student.id, e.target.value)}
                                  placeholder="הוסף הערה..."
                                  className="bg-transparent border-b border-transparent focus:border-indigo-300 outline-none w-full text-sm py-0.5 transition-all"
                                />
                                {isSaving && (
                                  <Loader2 size={12} className="animate-spin text-indigo-400 absolute left-0" />
                                )}
                              </div>
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-slate-100">
                {filteredStudents.map((student, index) => {
                  const showHeader = index === 0 || (
                    viewGrouping === 'transport'
                      ? (student.transportName || 'ללא הסעה') !== (filteredStudents[index - 1].transportName || 'ללא הסעה')
                      : (selectedClassId === 'all' && student.className !== filteredStudents[index - 1].className)
                  );
                  const isSaving = savingStudentIds[student.id];

                  return (
                    <React.Fragment key={student.id}>
                      {showHeader && (
                        <div className="bg-slate-100 px-4 py-2 font-bold text-slate-700 text-sm border-y border-slate-200">
                          {viewGrouping === 'transport' ? (
                            <span className="text-amber-800">מסיע / קו: {student.transportName || 'ללא שיוך להסעה'}</span>
                          ) : (
                            <span className="text-indigo-800">כיתה {student.className}</span>
                          )}
                        </div>
                      )}
                      <div className="p-4 space-y-3">
                        <div className="flex items-center gap-4">
                          <button
                            onClick={() => toggleAttendance(student.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-2xl transition-all border-2 shadow-sm shrink-0 ${
                              student.present
                                ? 'bg-emerald-50 border-emerald-100 text-emerald-700'
                                : 'bg-red-50 border-red-100 text-red-700'
                            }`}
                          >
                            {student.present ? (
                              <>
                                <UserCheck size={16} />
                                <span className="text-xs font-bold">נוכח</span>
                              </>
                            ) : (
                              <>
                                <UserX size={16} />
                                <span className="text-xs font-bold">נעדר</span>
                              </>
                            )}
                          </button>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-slate-900">{student.fullName}</span>
                              {isSaving && (
                                <Loader2 size={13} className="animate-spin text-indigo-500" />
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-1.5 mt-1">
                              <span className="px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded text-[10px] font-bold">
                                {student.className}
                              </span>
                              {student.transportName && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-800 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                                  <Bus size={10} className="text-amber-600" />
                                  {student.transportName}
                                </span>
                              )}
                              {student.escortName && (
                                <span className="text-[10px] text-slate-500">
                                  (מלווה: {student.escortName})
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="relative">
                          <input
                            type="text"
                            value={student.notes}
                            onChange={(e) => updateNotes(student.id, e.target.value)}
                            placeholder="הוסף הערה..."
                            className="w-full px-4 py-2 bg-slate-50 border border-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                          />
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </>
          )}
        </motion.div>
      )}

      <AnimatePresence>
        {showBulkModal && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              <div className="p-6 border-b border-slate-100 bg-amber-50">
                <h3 className="text-xl font-bold text-amber-900 flex items-center gap-2">
                  <AlertCircle size={24} />
                  דיווח היעדרות גורפת
                </h3>
                <p className="text-sm text-amber-700 mt-1">
                  פעולה זו תסמן את כל {studentsWithAttendance.length} התלמידים המוצגים כ"נעדרים" עם סיבה מוגדרת.
                </p>
              </div>
              <div className="p-6 space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700">סיבת ההיעדרות</label>
                  <div className="grid grid-cols-2 gap-2">
                    {['חג', 'שביתה', 'מלחמה', 'מזג אוויר', 'אחר'].map(reason => (
                      <button
                        key={reason}
                        onClick={() => setBulkReason(reason)}
                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-all border ${
                          bulkReason === reason 
                            ? 'bg-indigo-600 text-white border-indigo-600 shadow-md' 
                            : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'
                        }`}
                      >
                        {reason}
                      </button>
                    ))}
                  </div>
                </div>

                {bulkReason === 'אחר' && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="space-y-2"
                  >
                    <label className="text-sm font-bold text-slate-700">פרט סיבה</label>
                    <input
                      type="text"
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                      placeholder="הקלד סיבה..."
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                    />
                  </motion.div>
                )}

                <div className="flex gap-3 pt-4">
                  <button
                    onClick={applyBulkAbsence}
                    className="flex-1 bg-indigo-600 text-white font-bold py-3 rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100"
                  >
                    החל על כולם
                  </button>
                  <button
                    onClick={() => setShowBulkModal(false)}
                    className="flex-1 bg-slate-100 text-slate-600 font-bold py-3 rounded-xl hover:bg-slate-200 transition-all"
                  >
                    ביטול
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {message.text && (
          <motion.div 
            initial={{ opacity: 0, y: 50, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: 20, x: '-50%' }}
            className={`fixed bottom-8 left-1/2 px-8 py-4 rounded-2xl shadow-2xl text-white font-bold z-50 flex items-center gap-3 ${
              message.type === 'success' ? 'bg-emerald-600' : 'bg-red-600'
            }`}
          >
            {message.type === 'success' ? <Check size={20} /> : <AlertCircle size={20} />}
            {message.text}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Attendance;
